/**
 * Capture times are camera-local wall clocks. Do not pass them through `new Date(iso)`:
 * timezone-less ISO and `Z` strings would shift the hour in the viewer's timezone.
 */
const CAMERA_LOCAL_PATTERN =
  /^(\d{4})[-:](\d{2})[-:](\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?$/i;

export type CameraLocalDateTime = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

export function parseCameraLocalDateTime(raw: string | undefined | null): CameraLocalDateTime | undefined {
  if (!raw) {
    return undefined;
  }
  const value = raw.trim();
  if (!value) {
    return undefined;
  }

  const match = CAMERA_LOCAL_PATTERN.exec(value);
  if (!match) {
    return undefined;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4] ?? 0);
  const minute = Number(match[5] ?? 0);
  const second = Number(match[6] ?? 0);
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    hour > 23 ||
    minute > 59 ||
    second > 60
  ) {
    return undefined;
  }

  return { year, month, day, hour, minute, second };
}

export function formatCameraLocalDateTime(parts: CameraLocalDateTime): string {
  const month = String(parts.month).padStart(2, "0");
  const day = String(parts.day).padStart(2, "0");
  const hour = String(parts.hour).padStart(2, "0");
  const minute = String(parts.minute).padStart(2, "0");
  const second = String(parts.second).padStart(2, "0");
  return `${parts.year}-${month}-${day}T${hour}:${minute}:${second}`;
}

export function toCameraLocalDateTime(raw: string | undefined | null): string | undefined {
  const parsed = parseCameraLocalDateTime(raw);
  return parsed ? formatCameraLocalDateTime(parsed) : undefined;
}

export function cameraLocalDayKey(raw: string | undefined | null): string | null {
  const parsed = parseCameraLocalDateTime(raw);
  if (!parsed) {
    return null;
  }
  const month = String(parsed.month).padStart(2, "0");
  const day = String(parsed.day).padStart(2, "0");
  return `${parsed.year}-${month}-${day}`;
}

export function formatTakenAt(raw: unknown): string {
  if (!raw) {
    return "Unknown";
  }
  if (raw instanceof Date) {
    return raw.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  if (typeof raw !== "string") {
    return String(raw);
  }

  const parsed = parseCameraLocalDateTime(raw);
  if (parsed) {
    const dt = new Date(parsed.year, parsed.month - 1, parsed.day, parsed.hour, parsed.minute, parsed.second);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  }

  return raw.trim() || "Unknown";
}

export function formatInstant(raw: unknown): string {
  if (!raw) {
    return "Unknown";
  }
  if (raw instanceof Date) {
    return raw.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  if (typeof raw !== "string") {
    return String(raw);
  }

  const dt = new Date(raw);
  if (!Number.isNaN(dt.getTime())) {
    return dt.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return raw.trim() || "Unknown";
}
